import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import NotificationCenter from '../../components/ui/NotificationCenter';
import NodeLibrary from './components/NodeLibrary';
import WorkflowCanvas from './components/WorkflowCanvas';
import PropertiesPanel from './components/PropertiesPanel';
import WorkflowToolbar from './components/WorkflowToolbar';
import WorkflowTemplates from './components/WorkflowTemplates';
import TemplateLibrary from './components/TemplateLibrary';
import Icon from '../../components/AppIcon';

import workflowAutosaveService from '../../services/workflowAutosaveService';
import workflowTemplateService from '../../services/workflowTemplateService';
import workflowSaveService from '../../services/workflowSaveService';
import ActiveEditorsPanel from './components/ActiveEditorsPanel';
import ConflictDetectionModal from './components/ConflictDetectionModal';
import Breadcrumb from '../module-landing-page/components/Breadcrumb';
import projectService from '../../services/projectService';
import projectDashboardService from '../../services/projectDashboardService';





const VisualWorkflowDesigner = () => {
  const location = useLocation();
  const urlParams = useParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [nodeLibraryCollapsed, setNodeLibraryCollapsed] = useState(false);
  const [templateLibraryCollapsed, setTemplateLibraryCollapsed] = useState(false);
  const [propertiesPanelCollapsed, setPropertiesPanelCollapsed] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [currentWorkflow, setCurrentWorkflow] = useState('workflow-1');
  const [nodes, setNodes] = useState([]);
  const [connections, setConnections] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isSaving, setIsSaving] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedNodes, setSelectedNodes] = useState(new Set());
  const [lastAutosaveTime, setLastAutosaveTime] = useState(null);
  const [autosaveStatus, setAutosaveStatus] = useState('idle'); // idle, saving, saved, error
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [versionForm, setVersionForm] = useState({ name: '', description: '' });
  const [editingTemplate, setEditingTemplate] = useState(null); // Track which template is being edited
  const [activeEditors, setActiveEditors] = useState([]);
  const [currentUserEmail, setCurrentUserEmail] = useState('user@example.com');
  const [conflicts, setConflicts] = useState([]);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const workflows = [
    {
      id: 'workflow-1',
      name: 'Main Assembly Line A',
      nodeCount: 0,
      lastModified: '2 hours ago'
    },
    {
      id: 'workflow-2',
      name: 'Sub-Production Line B',
      nodeCount: 0,
      lastModified: '1 day ago'
    },
    {
      id: 'workflow-3',
      name: 'Quality Control Flow',
      nodeCount: 0,
      lastModified: '3 days ago'
    }
  ];
  
  // Get projectId and moduleId from URL params or query params
  const { search } = location;
  const searchParams = new URLSearchParams(search);
  const params = location?.state?.params || {};
  const projectId = urlParams?.projectId || params?.projectId || searchParams?.get('projectId') || location?.state?.projectId || null;
  const moduleId = urlParams?.moduleId || params?.moduleId || searchParams?.get('moduleId') || location?.state?.moduleId || null;
  const moduleName = searchParams?.get('moduleName') || location?.state?.moduleName || 'Untitled Module';
  const projectName = location?.state?.projectName || 'Untitled Project';

  // State for project and module data
  const [projectData, setProjectData] = useState(null);
  const [moduleData, setModuleData] = useState(null);

  // Load project and module data
  useEffect(() => {
    const loadProjectAndModule = async () => {
      if (projectId) {
        try {
          const { data: project } = await projectService?.getProjectById(projectId);
          setProjectData(project);

          if (moduleId) {
            const allData = await projectDashboardService?.getAllDashboardData(projectId);
            const module = allData?.modules?.find(m => m?.id === moduleId);
            setModuleData(module);
          }
        } catch (err) {
          console.error('Error loading project/module data:', err);
        }
      }
    };
    loadProjectAndModule();
  }, [projectId, moduleId]);

  // Load workflow from module if moduleId is present
  useEffect(() => {
    const loadModuleWorkflow = async () => {
      if (moduleId) {
        const result = await workflowSaveService?.loadWorkflowFromModule(moduleId);
        
        if (result?.success && result?.data) {
          setNodes(result?.data?.nodes || []);
          setConnections(result?.data?.connections || []);
          setLastAutosaveTime(result?.data?.metadata?.savedAt);
          
          // Initialize history with loaded state
          if (result?.data?.nodes?.length > 0 || result?.data?.connections?.length > 0) {
            setHistory([{ nodes: result?.data?.nodes, connections: result?.data?.connections }]);
            setHistoryIndex(0);
          }
          
          console.log('Workflow loaded from module:', moduleId, 'Source:', result?.source || 'database');
        } else {
          console.log('No existing workflow for module:', moduleId);
        }
      }
    };

    loadModuleWorkflow();
  }, [moduleId]);

  // Load autosaved workflow on mount and when workflow changes
  useEffect(() => {
    const loadAutosave = () => {
      const result = workflowAutosaveService?.loadWorkflow(currentWorkflow, projectId);
      
      if (result?.success && result?.data) {
        setNodes(result?.data?.nodes || []);
        setConnections(result?.data?.connections || []);
        setLastAutosaveTime(result?.data?.metadata?.savedAt);
        
        // Initialize history with loaded state
        if (result?.data?.nodes?.length > 0 || result?.data?.connections?.length > 0) {
          setHistory([{ nodes: result?.data?.nodes, connections: result?.data?.connections }]);
          setHistoryIndex(0);
        }
        
        console.log('Workflow loaded from autosave:', currentWorkflow, 'for project:', projectId);
      }
    };

    loadAutosave();
  }, [currentWorkflow, projectId]);

  // Start autosave timer
  useEffect(() => {
    const getState = () => ({
      nodes,
      connections,
      metadata: {
        workflowName: workflows?.find(w => w?.id === currentWorkflow)?.name,
        zoomLevel,
        projectId,
        projectName
      }
    });

    const onSave = (result) => {
      if (result?.success) {
        setLastAutosaveTime(result?.savedAt);
        setAutosaveStatus('saved');
        
        // Reset status after 2 seconds
        setTimeout(() => {
          setAutosaveStatus('idle');
        }, 2000);
      } else {
        setAutosaveStatus('error');
        console.error('Autosave failed:', result?.error);
      }
    };

    workflowAutosaveService?.startAutosave(projectId, getState, onSave, projectId);

    return () => {
      workflowAutosaveService?.stopAutosave();
    };
  }, [projectId, nodes, connections, zoomLevel, projectName]);

  // Debounced save on state changes
  useEffect(() => {
    if (nodes?.length > 0 || connections?.length > 0) {
      setAutosaveStatus('saving');
      workflowAutosaveService?.debouncedSave(
        currentWorkflow,
        nodes,
        connections,
        {
          workflowName: workflows?.find(w => w?.id === currentWorkflow)?.name,
          zoomLevel
        },
        projectId
      );
      
      // Also save to module database if moduleId is present
      if (moduleId && projectId) {
        const workflowData = {
          nodes,
          connections,
          metadata: {
            workflowName: moduleName,
            projectId,
            projectName,
            moduleId,
            zoomLevel,
            lastModified: new Date()?.toISOString()
          }
        };
        
        // Debounced save to module database
        const saveToModule = async () => {
          const result = await workflowSaveService?.saveWorkflowToModule(
            moduleId,
            projectId,
            workflowData
          );
          if (result?.success) {
            console.log('Workflow auto-saved to module database:', moduleId, 'Version:', result?.version);
          }
        };
        
        // Debounce the module save
        const timeoutId = setTimeout(saveToModule, 2000);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [nodes, connections, currentWorkflow, projectId, moduleId, moduleName, projectName]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e?.ctrlKey || e?.metaKey) {
        if (e?.key === 'z') {
          e?.preventDefault();
          handleUndo();
        } else if (e?.key === 'y') {
          e?.preventDefault();
          handleRedo();
        } else if (e?.key === 's') {
          e?.preventDefault();
          handleSave();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [historyIndex, history]);

  const addToHistory = (newNodes, newConnections) => {
    const newHistory = history?.slice(0, historyIndex + 1);
    newHistory?.push({ nodes: newNodes, connections: newConnections });
    setHistory(newHistory);
    setHistoryIndex(newHistory?.length - 1);
  };

  const handleNodeAdd = (node) => {
    // Check if this is a batch template add
    if (node?.type === 'template-batch') {
      const newNodes = [...nodes, ...node?.nodes];
      const newConnections = [...connections, ...node?.connections];
      setNodes(newNodes);
      setConnections(newConnections);
      addToHistory(newNodes, newConnections);
    } else {
      // Single node add
      const newNodes = [...nodes, node];
      setNodes(newNodes);
      addToHistory(newNodes, connections);
    }
  };

  const handleNodeMove = (nodeId, position) => {
    const newNodes = nodes?.map(node =>
      node?.id === nodeId ? { ...node, position } : node
    );
    setNodes(newNodes);
  };

  const handleNodeUpdate = (nodeId, updatedNode) => {
    const newNodes = nodes?.map(node =>
      node?.id === nodeId ? updatedNode : node
    );
    setNodes(newNodes);
    addToHistory(newNodes, connections);
  };

  const handleNodeDelete = (nodeId) => {
    const newNodes = nodes?.filter(node => node?.id !== nodeId);
    const newConnections = connections?.filter(
      conn => conn?.source !== nodeId && conn?.target !== nodeId
    );
    setNodes(newNodes);
    setConnections(newConnections);
    setSelectedNodeId(null);
    addToHistory(newNodes, newConnections);
  };

  const handleGroupDelete = () => {
    if (selectedNodes?.size === 0) {
      alert('Please select at least one node to delete');
      return;
    }
    
    if (window.confirm(`Delete ${selectedNodes?.size} selected node(s)?`)) {
      const newNodes = nodes?.filter(node => !selectedNodes?.has(node?.id));
      const newConnections = connections?.filter(
        conn => !selectedNodes?.has(conn?.source) && !selectedNodes?.has(conn?.target)
      );
      setNodes(newNodes);
      setConnections(newConnections);
      setSelectedNodes(new Set());
      setSelectedNodeId(null);
      addToHistory(newNodes, newConnections);
    }
  };

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedNodes(new Set());
  };

  const selectAllNodes = () => {
    setSelectedNodes(new Set(nodes?.map(n => n?.id)));
  };

  const clearSelection = () => {
    setSelectedNodes(new Set());
  };

  const handleConnectionAdd = (connection) => {
    const newConnections = [...connections, connection];
    setConnections(newConnections);
    addToHistory(nodes, newConnections);
  };

  const handleConnectionDelete = (connectionId) => {
    const newConnections = connections?.filter(conn => conn?.id !== connectionId);
    setConnections(newConnections);
    addToHistory(nodes, newConnections);
  };

  // Listen for connection delete events from WorkflowCanvas
  useEffect(() => {
    const handleDeleteConnectionEvent = (e) => {
      if (e?.detail?.connectionId) {
        handleConnectionDelete(e?.detail?.connectionId);
      }
    };

    window.addEventListener('deleteConnection', handleDeleteConnectionEvent);
    return () => window.removeEventListener('deleteConnection', handleDeleteConnectionEvent);
  }, [connections]);

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setNodes(history?.[newIndex]?.nodes);
      setConnections(history?.[newIndex]?.connections);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history?.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setNodes(history?.[newIndex]?.nodes);
      setConnections(history?.[newIndex]?.connections);
    }
  };

  const handleSave = () => {
    setIsSaving(true);
    
    // Force immediate save to localStorage
    const result = workflowAutosaveService?.forceSave(
      currentWorkflow,
      nodes,
      connections,
      {
        workflowName: workflows?.find(w => w?.id === currentWorkflow)?.name,
        zoomLevel
      },
      projectId
    );
    
    // Also save to module database if moduleId is present
    if (moduleId && projectId) {
      const workflowData = {
        nodes,
        connections,
        metadata: {
          workflowName: moduleName,
          projectId,
          projectName,
          moduleId,
          zoomLevel,
          lastModified: new Date()?.toISOString(),
          nodeCount: nodes?.length,
          connectionCount: connections?.length
        }
      };
      
      workflowSaveService?.saveWorkflowToModule(moduleId, projectId, workflowData)?.then((moduleResult) => {
        if (moduleResult?.success) {
          console.log('Workflow saved to module database:', moduleId, 'Version:', moduleResult?.version);
        }
      });
    }
    
    setTimeout(() => {
      setIsSaving(false);
      if (result?.success) {
        setLastAutosaveTime(result?.savedAt);
        setAutosaveStatus('saved');
        console.log('Workflow manually saved:', { nodes, connections, moduleId, projectId });
      } else {
        setAutosaveStatus('error');
        console.error('Save failed:', result?.error);
      }
    }, 500);
  };

  const handleExport = (format) => {
    console.log('Exporting workflow as:', format);
  };

  const handleTemplateSelect = (template) => {
    console.log('Template selected:', template);
    
    // Add all nodes from the template
    if (template?.nodes && Array.isArray(template?.nodes)) {
      const nodeIdMap = {}; // Map old IDs to new IDs
      const newNodes = [];
      
      // Create new nodes with updated IDs
      template?.nodes?.forEach((templateNode, index) => {
        const newNodeId = `node-${Date.now()}-${index}-${Math.random()?.toString(36)?.substr(2, 9)}`;
        nodeIdMap[templateNode?.id] = newNodeId;
        
        const newNode = {
          id: newNodeId,
          type: templateNode?.type,
          name: templateNode?.name,
          icon: templateNode?.icon,
          color: templateNode?.color,
          position: {
            x: (templateNode?.position?.x || 0) + 100, // Offset to center of canvas
            y: (templateNode?.position?.y || 0) + 100
          },
          data: {
            ...templateNode?.data,
            isContainer: templateNode?.data?.isContainer || false
          }
        };
        newNodes?.push(newNode);
      });
      
      // Create new connections with updated IDs
      const newConnections = [];
      if (template?.connections && Array.isArray(template?.connections)) {
        template?.connections?.forEach((conn, index) => {
          newConnections?.push({
            id: `conn-${Date.now()}-${index}-${Math.random()?.toString(36)?.substr(2, 9)}`,
            source: nodeIdMap?.[conn?.source] || conn?.source,
            target: nodeIdMap?.[conn?.target] || conn?.target,
            type: conn?.type || 'sequence'
          });
        });
      }
      
      // Add all nodes and connections to canvas in one batch
      const updatedNodes = [...nodes, ...newNodes];
      const updatedConnections = [...connections, ...newConnections];
      setNodes(updatedNodes);
      setConnections(updatedConnections);
      addToHistory(updatedNodes, updatedConnections);
    }
  };

  const handleTemplateEdit = (template) => {
    console.log('Editing template:', template);
    
    // Load template nodes and connections into canvas for editing
    if (template?.nodes && Array.isArray(template?.nodes)) {
      setNodes(template?.nodes || []);
      setConnections(template?.connections || []);
      
      // Update history
      const newState = { nodes: template?.nodes || [], connections: template?.connections || [] };
      setHistory([newState]);
      setHistoryIndex(0);
      
      // Set editing template state
      setEditingTemplate(template);
      
      console.log('Template loaded for editing:', template?.name);
    }
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    
    // Check if trying to edit a system template
    if (editingTemplate?.is_system) {
      alert('System templates cannot be modified. Please create a copy of this template to make changes.');
      setIsSaving(false);
      return;
    }
    
    try {
      setIsSaving(true);
      
      // Update the template with current nodes and connections
      const result = await workflowTemplateService?.updateTemplate(editingTemplate?.id, {
        name: editingTemplate?.name,
        description: editingTemplate?.description,
        category: editingTemplate?.category,
        nodes: nodes,
        connections: connections
      });
      
      console.log('Template saved successfully:', editingTemplate?.name, result);
      
      // Clear editing state and canvas content
      setEditingTemplate(null);
      setNodes([]);
      setConnections([]);
      setHistory([]);
      setHistoryIndex(-1);
      setSelectedNodeId(null);
      setSelectedNodes(new Set());
      
      // Show success message
      alert(`Template "${editingTemplate?.name}" saved successfully!`);
    } catch (err) {
      console.error('Error saving template:', err);
      alert('Failed to save template: ' + (err?.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelTemplateEdit = () => {
    if (window.confirm('Discard changes to this template?')) {
      setEditingTemplate(null);
      setNodes([]);
      setConnections([]);
      setHistory([]);
      setHistoryIndex(-1);
    }
  };

  const handleConflictResolve = (resolvedConflict) => {
    console.log('Conflict resolved:', resolvedConflict);
    setConflicts(conflicts?.filter(c => c?.id !== resolvedConflict?.id));
    if (conflicts?.length <= 1) {
      setShowConflictModal(false);
    }
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.1, 2));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  const handleSaveWorkflow = () => {
    handleSave();
  };

  const handleImport = (data) => {
    console.log('Importing workflow:', data);
  };

  const handleToggleSelectionMode = () => {
    toggleSelectionMode();
  };

  const handleGroupSelected = () => {
    console.log('Grouping selected nodes');
  };

  const handleDeleteSelected = () => {
    handleGroupDelete();
  };

  const handleDuplicateSelected = () => {
    console.log('Duplicating selected nodes');
  };

  const handleAlignSelected = (alignment) => {
    console.log('Aligning selected nodes:', alignment);
  };

  const handleDistributeSelected = (distribution) => {
    console.log('Distributing selected nodes:', distribution);
  };

  const selectedNode = nodes?.find(node => node?.id === selectedNodeId);

  return (
    <div className="flex h-screen bg-[var(--color-background)]">
      {/* Sidebar */}
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden transition-all duration-250" style={{ marginLeft: projectId ? 'calc(var(--sidebar-width, 240px) + var(--project-submenu-width, 192px))' : 'var(--sidebar-width, 240px)' }}>
        {/* Top Toolbar */}
        <div className="border-b border-[var(--color-border)] bg-[var(--color-card)]">
          <div className="px-6 py-2">
            <Breadcrumb
              projectId={projectId}
              projectName={projectData?.name}
              moduleId={moduleId}
              moduleName={moduleData?.name}
              workflowName={moduleData?.name || 'Workflow Designer'}
              workflowLevel="Designer"
            />
          </div>
          
          {/* Workflow Header with Project Logo */}
          <div className="px-6 py-3 border-t border-[var(--color-border)]">
            <div className="flex items-center gap-4">
              {/* Project Logo/Image */}
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-[var(--color-muted)] flex items-center justify-center flex-shrink-0">
                {projectData?.image_url && !imageError ? (
                  <img
                    src={projectData?.image_url}
                    alt={projectData?.name || 'Project'}
                    className="w-full h-full object-cover"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <Icon name="folder" size={24} className="text-[var(--color-muted-foreground)]" />
                )}
              </div>

              {/* Workflow Info */}
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-[var(--color-foreground)]">
                  {moduleData?.name || 'Workflow Designer'}
                </h1>
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  {nodes?.length || 0} nodes • {connections?.length || 0} connections
                </p>
              </div>
            </div>
          </div>

          <WorkflowToolbar
            currentWorkflow={currentWorkflow}
            workflows={workflows}
            onWorkflowChange={setCurrentWorkflow}
            onUndo={handleUndo}
            onRedo={handleRedo}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onResetZoom={handleResetZoom}
            onZoomReset={handleResetZoom}
            onSave={handleSaveWorkflow}
            onExport={handleExport}
            onImport={handleImport}
            onOpenTemplates={() => setTemplatesOpen(true)}
            onOpenVersionHistory={() => setVersionHistoryOpen(true)}
            onVersionHistory={() => setVersionHistoryOpen(true)}
            canUndo={historyIndex > 0}
            canRedo={historyIndex < history?.length - 1}
            zoomLevel={zoomLevel}
            isSaving={isSaving}
            isSelectionMode={isSelectionMode}
            onToggleSelectionMode={handleToggleSelectionMode}
            selectedNodesCount={selectedNodes?.size}
            selectedCount={selectedNodes?.size}
            onGroupSelected={handleGroupSelected}
            onDeleteSelected={handleDeleteSelected}
            onDuplicateSelected={handleDuplicateSelected}
            onAlignSelected={handleAlignSelected}
            onDistributeSelected={handleDistributeSelected}
            onSelectAll={selectAllNodes}
            onClearSelection={clearSelection}
            onGroupDelete={handleGroupDelete}
            autosaveStatus={autosaveStatus}
            lastAutosaveTime={lastAutosaveTime}
            nodes={nodes}
            connections={connections}
          />
        </div>

        <div className="flex-1 flex overflow-hidden">
          <div className={`transition-all duration-250 ${nodeLibraryCollapsed ? 'w-16' : 'w-full lg:w-64'}`}>
            <NodeLibrary
              onNodeDragStart={(node) => console.log('Dragging node:', node)}
              isCollapsed={nodeLibraryCollapsed}
              onToggleCollapse={() => setNodeLibraryCollapsed(!nodeLibraryCollapsed)}
            />
          </div>

          <div className={`transition-all duration-250 border-r border-border ${templateLibraryCollapsed ? 'w-16' : 'w-full lg:w-80'}`}>
            <TemplateLibrary
              onTemplateDragStart={(template) => console.log('Dragging template:', template)}
              onTemplateSelect={handleTemplateSelect}
              onTemplateEdit={handleTemplateEdit}
              isCollapsed={templateLibraryCollapsed}
              onToggleCollapse={() => setTemplateLibraryCollapsed(!templateLibraryCollapsed)}
            />
          </div>

          <div className="flex-1 relative">
            {/* Template Editing Banner */}
            {editingTemplate && (
              <div className="absolute top-0 left-0 right-0 z-20 bg-primary text-primary-foreground px-6 py-3 shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Icon name="Edit" size={20} />
                    <div>
                      <p className="text-sm font-semibold">Editing Template: {editingTemplate?.name}</p>
                      <p className="text-xs opacity-90">Make changes to the nodes and connections, then save or cancel</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleCancelTemplateEdit}
                      className="px-4 py-2 rounded-lg bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground transition-colors duration-250 text-sm font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveTemplate}
                      disabled={isSaving}
                      className="px-4 py-2 rounded-lg bg-primary-foreground text-primary hover:bg-primary-foreground/90 transition-colors duration-250 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isSaving ? (
                        <>
                          <Icon name="Loader" size={16} className="animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Icon name="Save" size={16} />
                          Save Template
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <WorkflowCanvas
              nodes={nodes}
              connections={connections}
              onNodeSelect={setSelectedNodeId}
              onNodeMove={handleNodeMove}
              onNodeUpdate={handleNodeUpdate}
              onNodeAdd={handleNodeAdd}
              onConnectionAdd={handleConnectionAdd}
              onConnectionDelete={handleConnectionDelete}
              selectedNodeId={selectedNodeId}
              zoomLevel={zoomLevel}
              onZoomChange={setZoomLevel}
              isSelectionMode={isSelectionMode}
              selectedNodes={selectedNodes}
              setSelectedNodes={setSelectedNodes}
            />

            <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
              {/* Selection Mode Controls */}
              <div className="flex items-center gap-2 bg-card rounded-xl shadow-lg border border-border px-4 py-2">
                <button
                  onClick={toggleSelectionMode}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors duration-250 ${
                    isSelectionMode ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground hover:bg-muted'
                  }`}
                >
                  <span className="text-sm font-medium">{isSelectionMode ? 'Exit Selection' : 'Select Nodes'}</span>
                </button>
                {isSelectionMode && (
                  <>
                    <div className="w-px h-6 bg-border" />
                    <button
                      onClick={selectAllNodes}
                      disabled={nodes?.length === 0}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background text-foreground hover:bg-muted transition-colors duration-250 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-sm">Select All</span>
                    </button>
                    <button
                      onClick={clearSelection}
                      disabled={selectedNodes?.size === 0}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background text-foreground hover:bg-muted transition-colors duration-250 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-sm">Clear</span>
                    </button>
                    <button
                      onClick={handleGroupDelete}
                      disabled={selectedNodes?.size === 0}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors duration-250 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-sm">Delete ({selectedNodes?.size})</span>
                    </button>
                  </>
                )}
              </div>
              <NotificationCenter />
            </div>

            <div className="absolute bottom-4 left-4 z-10 flex items-center gap-2 bg-card rounded-xl shadow-lg border border-border px-4 py-2">
              <button
                onClick={() => setTemplatesOpen(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors duration-250"
              >
                <span className="text-sm font-medium">Templates</span>
              </button>
              <div className="w-px h-6 bg-border" />
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{nodes?.length} nodes</span>
                <span>•</span>
                <span>{connections?.length} connections</span>
              </div>
            </div>
          </div>

          <div className={`transition-all duration-250 ${propertiesPanelCollapsed ? 'w-16' : 'w-full lg:w-80'}`}>
            <PropertiesPanel
              selectedNode={selectedNode}
              onNodeUpdate={handleNodeUpdate}
              onNodeDelete={handleNodeDelete}
              isCollapsed={propertiesPanelCollapsed}
              onToggleCollapse={() => setPropertiesPanelCollapsed(!propertiesPanelCollapsed)}
            />
          </div>
        </div>
      </div>
      <WorkflowTemplates
        isOpen={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        onTemplateSelect={handleTemplateSelect}
        onTemplateEdit={handleTemplateEdit}
      />

      {/* Active Editors Panel */}
      <ActiveEditorsPanel 
        activeEditors={activeEditors}
        currentUserEmail={currentUserEmail}
      />

      {/* Conflict Detection Modal */}
      <ConflictDetectionModal
        conflicts={conflicts}
        onResolve={handleConflictResolve}
        onClose={() => setShowConflictModal(false)}
      />

      {/* Version History Dialog */}
      {showVersionDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowVersionDialog(false)}></div>
          <div className="relative z-10 w-full max-w-3xl bg-card border border-border rounded-lg shadow-xl p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">Version History</h2>
              <button onClick={() => setShowVersionDialog(false)} className="text-primary-foreground hover:underline">
                Close
              </button>
            </div>
            <div className="mt-4 space-y-2">
              {history?.length === 0 ? (
                <div className="text-center text-muted-foreground">No versions found.</div>
              ) : (
                <div className="space-y-2">
                  {history?.map((entry, index) => {
                    const isCurrent = index === historyIndex;
                    return (
                      <div
                        key={index}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          isCurrent ? 'bg-primary text-primary-foreground border border-border' : 'bg-background text-foreground hover:bg-muted'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Icon name="History" size={16} className={isCurrent ? 'text-primary' : 'text-muted-foreground'} />
                          <span className="text-sm font-medium">{entry?.metadata?.workflowName}</span>
                          <span className="text-xs opacity-90">•</span>
                          <span className="text-xs opacity-90">{entry?.metadata?.lastModified}</span>
                        </div>
                        <button
                          onClick={() => {
                            setNodes(entry?.nodes || []);
                            setConnections(entry?.connections || []);
                            setHistoryIndex(index);
                            setShowVersionDialog(false);
                          }}
                          className="text-primary-foreground hover:underline"
                        >
                          View
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisualWorkflowDesigner;